import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "react-aria-components";
import documentIcon from "@/assets/figma/chat-file-drop/document-file.svg";
import imageIcon from "@/assets/figma/chat-file-drop/image-file.svg";
import videoIcon from "@/assets/figma/chat-file-drop/video-file.svg";
import { useGetMyFilesDetailQuery, useGetMyFilesResourceAccessUrlMutation } from "@/features/my-files/myFilesApi";
import { ChatAttachmentPreviewOverlay } from "./ChatAttachmentPreviewOverlay";
import type { ChatAttachmentResourceRef } from "./chatAttachments";

export function ConversationSentResources({ resources, organizationId, onOpenResource, onPreview }: {
  resources: ReadonlyArray<ChatAttachmentResourceRef>;
  organizationId: string;
  onOpenResource(id: string): void;
  onPreview?(id: string, url: string): void;
}) {
  return <div className="conversation-sent-resources" data-conversation-attachments="">
    {resources.map((resource) => <SentResource key={`${organizationId}:${resource.resource_id}`} resource={resource}
      organizationId={organizationId} onOpenResource={onOpenResource} onPreview={onPreview} />)}
  </div>;
}

function SentResource({ resource, organizationId, onOpenResource, onPreview }: {
  resource: ChatAttachmentResourceRef;
  organizationId: string;
  onOpenResource(id: string): void;
  onPreview?(id: string, url: string): void;
}) {
  const detail = useGetMyFilesDetailQuery({ organizationId, resourceId: resource.resource_id });
  const [access] = useGetMyFilesResourceAccessUrlMutation();
  const [preview, setPreview] = useState<{ url: string; expiresAt: number } | null>(null);
  const [failed, setFailed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const pendingPreview = useRef<Promise<string> | null>(null);
  // currentData prevents an old organization's cached resource from flashing on a scope change.
  const data = detail.currentData?.resource;
  const canPreview = data?.availableActions.includes("preview") && data.resourceType === "image";
  const requestUrl = useCallback(async (download = false) => {
    if (!data || data.resourceId !== resource.resource_id ||
      !data.availableActions.includes(download ? "download" : "preview")) throw new Error("资源不可用");
    const result = await access({ organizationId, resourceId: resource.resource_id, download }).unwrap();
    if (result.resourceId !== resource.resource_id || !/^https?:\/\//u.test(result.url) ||
      Date.parse(result.expiresAt) <= Date.now()) throw new Error("资源链接不可用");
    return result;
  }, [access, data, organizationId, resource.resource_id]);
  const requestPreview = useCallback(() => {
    // The thumbnail action and opening modal share one resource URL read.
    if (pendingPreview.current) return pendingPreview.current;
    const request = (async () => {
      const result = await requestUrl();
      setFailed(false);
      const expiresAt = Date.parse(result.expiresAt);
      setPreview((current) => current?.url === result.url && current.expiresAt === expiresAt
        ? current : { url: result.url, expiresAt });
      return result.url;
    })();
    pendingPreview.current = request;
    const clear = () => { if (pendingPreview.current === request) pendingPreview.current = null; };
    void request.then(clear, clear);
    return request;
  }, [requestUrl]);
  useEffect(() => {
    if (!canPreview) return;
    let active = true;
    void requestUrl().then((result) => {
      if (active) setPreview({ url: result.url, expiresAt: Date.parse(result.expiresAt) });
    }).catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [canPreview, requestUrl]);
  const unavailable = Boolean(detail.isError) || (!detail.isFetching && !data);
  const name = data?.displayName ?? (unavailable ? "附件不可用" : "正在读取附件");
  const icon = resource.resource_type === "image" ? imageIcon : resource.resource_type === "video" ? videoIcon : documentIcon;
  useEffect(() => {
    if (!preview) return;
    let timer: number;
    const checkExpiry = () => {
      const remaining = preview.expiresAt - Date.now();
      if (remaining <= 0) setPreview(null);
      else timer = window.setTimeout(checkExpiry, Math.min(2_147_483_647, remaining));
    };
    checkExpiry();
    return () => window.clearTimeout(timer);
  }, [preview]);
  const validPreview = preview && !failed ? preview.url : "";
  useEffect(() => { onPreview?.(resource.resource_id, validPreview); }, [onPreview, resource.resource_id, validPreview]);
  return <>
    <Button className="conversation-sent-resource" aria-label={`查看附件 ${name}`}
      isDisabled={unavailable || !data} onPress={() => {
        if (canPreview) { setIsOpen(true); if (!validPreview) { setFailed(false); void requestPreview().catch(() => setFailed(true)); } }
        else onOpenResource(resource.resource_id);
      }}>
      <img alt="" src={validPreview || icon} onError={() => setFailed(true)} />
      <span>{name}</span>{failed ? <small>预览暂时无法读取，点击重试</small> : null}
    </Button>
    {detail.isError ? <Button onPress={() => void detail.refetch()}>重新读取附件</Button> : null}
    {isOpen && data ? <ChatAttachmentPreviewOverlay
      attachments={[{ attachmentId: data.resourceId, resourceId: data.resourceId, displayName: name, resourceType: data.resourceType, status: "ready" }]}
      selectedAttachmentId={data.resourceId} previewUrls={{ [data.resourceId]: preview?.url ?? "" }}
      onSelect={() => {}} onClose={() => setIsOpen(false)} onRequestPreviewUrl={requestPreview}
      onRequestDownloadUrl={async () => (await requestUrl(true)).url} /> : null}
  </>;
}
